import React from "react";
import { isEmpty, orderBy } from "lodash";
import moment from "moment";

import DataBlock from "containers/underwriting/components/dataBlock";
import { getFormattedDate, getFullName } from "helpers";
import {
	AddressType,
	AuroraOutcomeStatuses,
	CheckingResponseDataType,
	GiactVerificationResponse,
	GofacMatchLevels,
	GofacResponseDataType,
	GverifyGauthenticateResponseDataType,
	UWCheckResultMode,
	labelType,
	matchReportColumnType,
	matchReportComparisonColumnType,
	matchResponseDataType,
} from "types";
import { SSNNormalizer, onlyNumberPhone, taxIdNormalizer } from "validators";

export const LABELS_GOFAC_RESULT = [
	{ value: "unique_id", name: "Unique ID" },
	{ value: "address_line1", name: "Address 1" },
	{ value: "city", name: "City" },
	{ value: "region_code", name: "State" },
	{ value: "postal_code", name: "Zip" },
	{ value: "country", name: "Country" },
	{ value: "item_reference_id", name: "Item Reference ID" },
	{
		value: "created_at",
		name: "Created Date",
		getValue: value => value && getFormattedDate({ date: value }),
	},
	{
		value: "verification_response",
		name: "Verification Response",
		getValue: value =>
			GiactVerificationResponse.getByName(value)?.displayName,
	},
	{
		value: "aurora_outcome",
		name: "Aurora Outcome",
		options: AuroraOutcomeStatuses.getOptions(),
		getValue: value => AuroraOutcomeStatuses.getByName(value)?.displayName,
	},
];

export const LABELS_GOFAC_RESULT_DBA_NAME = [
	{ value: "business_name", name: "DBA Name Checked" },
	...LABELS_GOFAC_RESULT,
];

export const LABELS_GOFAC_RESULT_LEGAL_NAME = [
	{ value: "business_name", name: "Legal Name Checked" },
	...LABELS_GOFAC_RESULT,
];

export const LABELS_GOFAC_RESULT_OWNER = [
	{
		value: "first_name",
		name: "Owner / Signer Name Checked",
		getValue: (_value, obj) => getFullName(obj),
	},
	{ value: "unique_id", name: "Unique ID" },
	{ value: "address_line1", name: "Address 1" },
	{ value: "city", name: "City" },
	{ value: "region_code", name: "State" },
	{ value: "postal_code", name: "Zip" },
	{
		value: "tax_id",
		name: "Tax ID",
		getValue: value => taxIdNormalizer(value),
	},
	{
		value: "date_of_birth",
		name: "Date of Birth",
		getValue: value =>
			value &&
			getFormattedDate({
				date: value,
				format: "DD MMM YYYY",
				keepLocalTime: true,
			}),
	},
	{ value: "item_reference_id", name: "Item Reference ID" },
	{
		value: "created_at",
		name: "Created Date",
		getValue: value => value && getFormattedDate({ date: value }),
	},
	{
		value: "verification_response",
		name: "Verification Response",
		getValue: value =>
			GiactVerificationResponse.getByName(value)?.displayName,
	},
	{
		value: "outcome",
		name: "Aurora Outcome",
		options: AuroraOutcomeStatuses.getOptions(),
		getValue: value => AuroraOutcomeStatuses.getByName(value)?.displayName,
	},
];

export const LABELS_GIDENTIFY_RESULT_OWNER = [
	{ value: "first_name", name: "First Name" },
	{ value: "last_name", name: "Last Name" },
	{ value: "address_line1", name: "Address Line 1" },
	{ value: "city", name: "City" },
	{ value: "region_code", name: "State" },
	{ value: "postal_code", name: "Zip" },
	{
		value: "tax_id",
		name: "SSN",
		getValue: value => value && SSNNormalizer(value),
	},
	{
		value: "date_of_birth",
		name: "Date of Birth",
		getValue: value =>
			value &&
			getFormattedDate({
				date: value,
				format: "DD MMM YYYY",
				keepLocalTime: true,
			}),
	},
	{
		value: "outcome",
		name: "Aurora Outcome",
		options: AuroraOutcomeStatuses.getOptions(),
		getValue: value => AuroraOutcomeStatuses.getByName(value)?.displayName,
	},
];

export const LABELS_GIDENTIFY_VERIFICATION_RESULT = [
	{
		value: "customer_response_code",
		name: "Person Identification Response Code",
		getValue: (value, { customer_response_code_description }) => (
			<div>
				<div>{value}</div>
				<div>{customer_response_code_description}</div>
			</div>
		),
	},
	{
		value: "verification_response",
		name: "Verification Response",
		getValue: value =>
			GiactVerificationResponse.getByName(value)?.displayName,
	},
];

export const LABELS_GIDENTIFY_MATCHED_RECORD = [
	{ value: "first_name", name: "First Name" },
	{ value: "last_name", name: "Last Name" },
	{
		value: "date_of_birth",
		name: "Date of Birth",
		getValue: value =>
			value &&
			getFormattedDate({
				date: value,
				format: "DD MMM YYYY",
				keepLocalTime: true,
			}),
	},
	{ value: "ssn_issue_state", name: "SSN Issue State" },
	{
		value: "ssn_issued_from",
		name: "SSN Issued",
		getValue: (value, { ssn_issued_to }) => {
			const from = getFormattedDate({ date: value, format: "YYYY" });
			const to = getFormattedDate({
				date: ssn_issued_to,
				format: "YYYY",
			});

			return `${from} - ${to}`;
		},
	},
	// { value: "alternate_names", name: "Alternate Names" },
	{
		value: "addresses",
		name: "Address Records",
		getValue: value => {
			const addresses = getAddressRecords(value);
			return <DataBlock id="addresses" data={addresses} />;
		},
	},
	{
		value: "phone_numbers",
		name: "Phone Number Records",
		getValue: value => {
			const phoneNumbers = getPhoneNumberRecords(value);
			return <DataBlock id="phone_numbers" data={phoneNumbers} />;
		},
	},
];

export const LABELS_MATCHES = [
	{ value: "entity_id", name: "Entity ID" },
	{ value: "list_item_name", name: "List Item Name" },
	{ value: "first_name", name: "First Name" },
	{ value: "last_name", name: "Last Name" },
	{ value: "remarks", name: "Remarks" },
	{ value: "data_source", name: "Data Source" },
	{
		value: "match_level",
		name: "Match Level",
		getValue: value => GofacMatchLevels.getByName(value)?.displayName,
	},
	{
		value: "match_aka",
		name: "Match Aka",
		getValue: value => (value ? "Yes" : "No"),
	},
	{ value: "match_details", name: "Match Details" },
	{
		value: "also_known_as",
		name: "Also Known As",
		getValue: value => arrayToString(value),
	},
	{
		value: "program_names",
		name: "Program Names",
		getValue: value => arrayToString(value),
	},
	{
		value: "associations",
		name: "Associations",
		getValue: value => arrayToString(value),
	},
	{
		value: "addresses",
		name: "Addresses",
		getValue: value => getAddressesString(value),
	},
	{
		value: "date_of_birth_listings",
		name: "Date Of Birth Listings",
		getValue: value =>
			value
				?.map(
					item =>
						item &&
						getFormattedDate({ date: item, format: "DD MMM YYYY" }),
				)
				.join(", "),
	},
	{
		value: "place_of_birth_listings",
		name: "Place Of Birth Listings",
		getValue: value => arrayToString(value),
	},
	{
		value: "nationalities",
		name: "Nationalities",
		getValue: value => arrayToString(value),
	},
	{
		value: "citizenships",
		name: "Citizenships",
		getValue: value => arrayToString(value),
	},
	{
		value: "passports",
		name: "Passports",
		getValue: value => arrayToString(value),
	},
	{
		value: "last_updated_date",
		name: "Last Updated Date",
		getValue: value =>
			value && getFormattedDate({ date: value, format: "MM/DD/YYYY" }),
	},
];

export const LABELS_GVERIFY_RESULT = [
	{ value: "dba_name", name: "DBA Name" },
	{ value: "legal_name", name: "Legal Name" },
	{ value: "unique_id", name: "Unique ID" },
	{ value: "item_reference_id", name: "Item Reference ID" },
	{
		value: "created_date",
		name: "Created Date",
		getValue: value => value && getFormattedDate({ date: value }),
	},
	{
		value: "verification_response",
		name: "Verification Response",
		getValue: value =>
			GiactVerificationResponse.getByName(value)?.displayName,
	},
	{
		value: "account_response_code",
		name: "Account Response Code",
		tooltip: "account_response_code_description",
	},
	{
		value: "aurora_outcome",
		name: "Aurora Outcome",
		options: AuroraOutcomeStatuses.getOptions(),
		getValue: value => AuroraOutcomeStatuses.getByName(value)?.displayName,
	},
	{ value: "bank_name", name: "Bank Name" },
	{ value: "dda_account_number", name: "DDA Account Number" },
	{ value: "aba_routing_number", name: "ABA Routing Number" },
	{
		value: "customer_response_code",
		name: "Customer Response Code",
		tooltip: "customer_response_code_description",
	},
	{ value: "results_passed_against", name: "Result Passed Against" },
];

export const LABELS_TIN_CHECK_RESULT = [
	{
		value: "created_date",
		name: "Date & time of check",
		getValue: value => value && getFormattedDate({ date: value }),
	},
	{ value: "legal_name", name: "Legal Name" },
	{
		value: "tax_id",
		name: "Tax ID",
		getValue: value => taxIdNormalizer(value),
	},
	{
		value: "aurora_outcome",
		name: "Aurora Outcome",
		options: AuroraOutcomeStatuses.getOptions(),
		getValue: value => AuroraOutcomeStatuses.getByName(value)?.displayName,
	},
];

export const LABELS_MATCH_RESULT = [
	{
		value: "created_at",
		name: "Date & Time of Inquiry",
		getValue: value =>
			value && moment.utc(value).local().format("DD MMMM YYYY h:mm A"),
	},
	{ value: "legal_name", name: "Legal Name (Merchant Name)" },
	{ value: "dba_name", name: "Doing Business As Name (DBA)" },
	{ value: "city", name: "City" },
	{ value: "region_code", name: "State" },
	{ value: "reference_number", name: "Reference Number" },
	{
		value: "outcome",
		name: "Aurora Outcome",
		options: AuroraOutcomeStatuses.getOptions(),
		getValue: value => AuroraOutcomeStatuses.getByName(value)?.displayName,
	},
	{ value: "outcome_details", name: "Details" },
];

export const LABELS_COMPARISON_MERCHANT_RESULT = [
	{ value: "legal_name", name: "Legal name (Merchant name)" },
	{ value: "dba_name", name: "Doing Business As Name(DBA)" },
	{ value: "phone_number", name: "Business Phone Number" },
	{
		value: "national_tax_id",
		name: "National Tax ID",
		getValue: value => {
			if (typeof value === "string" && !value.toString().includes("*")) {
				return taxIdNormalizer(value);
			} else {
				return value;
			}
		},
	},
	{ value: "address_line1", name: "Address Line 1" },
	{ value: "address_line2", name: "Address Line 2" },
	{ value: "city", name: "City" },
	{ value: "region_code", name: "State" },
	{ value: "postal_code", name: "Zip Code (Postal Code)" },
	{ value: "reason_code", name: "Reason Code" },
	{ value: "merchant_id", name: "Merchant ID" },
	{ value: "acquirer_id", name: "Acquirer ID" },
	{ value: "merchant_category_code", name: "Merchant Category Code (MCC)" },
	{
		value: "merchant_category_code_description",
		name: "MCC Code Description",
	},
	{ value: "comments", name: "Comments" },
	{
		value: "opened_at",
		name: "Date Opened",
		getValue: value => {
			return (
				value &&
				getFormattedDate({
					date: value || value.value,
					format: "DD MMMM YYYY",
				})
			);
		},
	},
	{
		value: "closed_at",
		name: "Date Closed",
		getValue: value => {
			return (
				value &&
				getFormattedDate({
					date: value || value.value,
					format: "DD MMMM YYYY",
				})
			);
		},
	},
	{ value: "reference_number", name: "Reference Number" },
];

export const LABELS_COMPARISON_INQUIRY_MATCH_RESULT = [
	{ value: "legal_name", name: "Legal name (Merchant name)" },
	{ value: "dba_name", name: "Doing Business As Name(DBA)" },
	{ value: "phone_number", name: "Business Phone Number" },
	{
		value: "national_tax_id",
		name: "National Tax ID",
		getValue: value => {
			if (typeof value === "string" && !value.toString().includes("*")) {
				return taxIdNormalizer(value);
			} else {
				return value;
			}
		},
	},
	{ value: "address_line1", name: "Address Line 1" },
	{ value: "address_line2", name: "Address Line 2" },
	{ value: "city", name: "City" },
	{ value: "region_code", name: "State" },
	{ value: "postal_code", name: "Zip Code (Postal Code)" },
	{ value: "acquirer_id", name: "Acquirer ID" },
	{
		value: "opened_at",
		name: "Date Opened",
		getValue: value => {
			return (
				value &&
				getFormattedDate({
					date: value || value.value,
					format: "DD MMMM YYYY",
				})
			);
		},
	},
	{
		value: "closed_at",
		name: "Date Closed",
		getValue: value => {
			return (
				value &&
				getFormattedDate({
					date: value || value.value,
					format: "DD MMMM YYYY",
				})
			);
		},
	},
	{ value: "reference_number", name: "Reference Number" },
];

export const LABELS_COMPARISON_OWNER_RESULT = [
	{ value: "first_name", name: "First Name" },
	{ value: "last_name", name: "Last Name" },
	{ value: "phone_number", name: "Phone Number" },
	{
		value: "national_id",
		name: "National ID (SSN)",
		getValue: value => {
			if (typeof value === "string" && !value.toString().includes("*")) {
				return SSNNormalizer(value);
			} else {
				return value;
			}
		},
	},
	{ value: "address_line1", name: "Address Line 1" },
	{ value: "address_line2", name: "Address Line 2" },
	{ value: "city", name: "City" },
	{ value: "region_code", name: "State" },
	{ value: "postal_code", name: "Zip Code (Postal Code)" },
	{ value: "country", name: "Country" },
];

const arrayToString = (array: any[]): string =>
	array?.filter(item => typeof item === "string").join(", ");

const getAddressesString = (addresses: AddressType[]): string =>
	addresses
		?.map(({ remarks, ...address }) =>
			Object.values(address)
				.filter(item => item)
				.join(", "),
		)
		.join("; ");

const getValueByKey = (
	obj: any,
	label: labelType,
	exception?: string,
): string | undefined => {
	let result;
	const key = label?.value;
	const getValue = label?.getValue;

	for (let property in obj) {
		if (obj.hasOwnProperty(property)) {
			if (property === exception) return;
			if (property === key) {
				return getValue ? getValue(obj[key], obj) : obj[key];
			} else if (typeof obj[property] === "object") {
				result = getValueByKey(obj[property], label);
			}
			if (typeof result !== "undefined") return result;
		}
	}
};

export const getResultTableData = (
	originalData: any,
	rerunData: any,
	labels: labelType[],
	exception?: string,
): matchReportColumnType[] =>
	labels.reduce((matches: matchReportColumnType[], label: labelType) => {
		const originalInquiry = getValueByKey(originalData, label, exception);
		const reRunInquiry = getValueByKey(rerunData, label, exception);
		const isMatch = originalInquiry === reRunInquiry;

		if (originalInquiry || reRunInquiry) {
			matches.push({
				name: label?.name,
				fieldName: label?.value,
				originalInquiry: {
					data: originalInquiry,
					tooltip: label?.tooltip ? originalData[label?.tooltip] : "",
				},
				reRunInquiry: {
					data: reRunInquiry,
					tooltip:
						label?.tooltip && reRunInquiry
							? rerunData[label?.tooltip]
							: "",
				},
				isMatch,
				options: label?.options,
			});
		}

		return matches;
	}, []);

export const getResultComparisonTableData = (
	inquiryData: any,
	matchesData: any,
	labels: labelType[],
	acquirerData?: { [key: string]: any },
): matchReportComparisonColumnType[] =>
	labels.reduce((matches: matchReportComparisonColumnType[], label: any) => {
		const originalInquiry: any = getValueByKey(inquiryData, label);
		const merchantMatchResults: any = getValueByKey(matchesData, label);
		const isMatch = () => {
			if (!isEmpty(originalInquiry) && !isEmpty(merchantMatchResults)) {
				if (originalInquiry === merchantMatchResults) {
					return true;
				} else if (originalInquiry === merchantMatchResults?.value)
					return true;
				else if (originalInquiry?.value === merchantMatchResults)
					return true;
				else if (originalInquiry?.value === merchantMatchResults?.value)
					return true;
				else return false;
			} else return false;
		};

		matches.push({
			name: label?.name,
			fieldName: label?.value,
			inquiry: { data: originalInquiry },
			merchantMatchResults: { data: merchantMatchResults },
			isMatch: isMatch(),
			acquirerData: acquirerData || {},
		});

		return matches;
	}, []);

export const getPotentialsMatches = (
	data: CheckingResponseDataType,
	labels: labelType[],
): {
	name: string;
	items: matchReportColumnType[];
}[] =>
	data?.original?.response?.potential_matches?.map((match, key) => {
		const items = getResultTableData(
			match,
			data?.rerun?.response?.potential_matches[key],
			labels,
		);

		const name = [match?.entity_id, match?.name?.list_item_name]
			.filter(item => item)
			.join(" - ");

		return {
			name,
			items,
		};
	}) || [];

export const getGofacSuccessValue = (data: GofacResponseDataType) => {
	const { dba_name, legal_name } = data || {};
	if (!data) {
		return { success: undefined, auroraOutcome: undefined };
	}

	const originalDba = dba_name?.original;
	const originalLegal = legal_name?.original;
	const rerunDba = dba_name?.rerun;
	const rerunLegal = legal_name?.rerun;
	let isSuccessDbaName, isSuccessLegalName;

	const auroraOutcome =
		data?.rerun_outcome === null
			? data?.original_outcome
			: data?.rerun_outcome;

	const dbaResultMode = rerunDba
		? rerunDba?.result_mode
		: originalDba?.result_mode;

	const legalResultMode = rerunLegal
		? rerunLegal?.result_mode
		: originalLegal?.result_mode;

	const resultMode =
		legalResultMode === UWCheckResultMode.Valid.name
			? dbaResultMode
			: legalResultMode;

	if (rerunDba && rerunLegal) {
		isSuccessDbaName = rerunDba?.is_success;
		isSuccessLegalName = rerunLegal?.is_success;
	} else {
		isSuccessDbaName = originalDba?.is_success;
		isSuccessLegalName = originalLegal?.is_success;
	}

	if (isSuccessDbaName && isSuccessLegalName) {
		return { success: true, auroraOutcome, resultMode };
	} else {
		return { success: false, auroraOutcome, resultMode };
	}
};

export const getOwnerGofacSuccessValue = (data: CheckingResponseDataType) => {
	const { original, rerun } = data || {};
	const isSuccessOriginal = original?.is_success;
	const isSuccessRerun = rerun?.is_success;
	if (rerun) {
		return {
			success: isSuccessRerun,
			auroraOutcome: rerun?.outcome,
			resultMode: rerun?.result_mode,
		};
	} else {
		return {
			success: isSuccessOriginal,
			auroraOutcome: original?.outcome,
			resultMode: original?.result_mode,
		};
	}
};

export const getOwnerGidentifySuccessValue = (
	data: CheckingResponseDataType,
) => {
	const { original, rerun } = data || {};
	const isSuccessOriginal = original?.is_success;
	const isSuccessRerun = rerun?.is_success;
	if (rerun) {
		return {
			success: isSuccessRerun,
			auroraOutcome: rerun?.outcome,
			resultMode: rerun?.result_mode,
		};
	} else {
		return {
			success: isSuccessOriginal,
			auroraOutcome: original?.outcome,
			resultMode: original?.result_mode,
		};
	}
};

export const getGverifyGauthenticateSuccessValue = (
	data: GverifyGauthenticateResponseDataType,
) => {
	const { original_inquiry, re_run_inquiry } = data || {};
	const isSuccessOriginal = original_inquiry?.is_success;
	const isSuccessRerun = re_run_inquiry?.is_success;
	if (re_run_inquiry) {
		return {
			success: isSuccessRerun,
			auroraOutcome: re_run_inquiry?.aurora_outcome,
			resultMode: re_run_inquiry?.result_mode,
		};
	} else {
		return {
			success: isSuccessOriginal,
			auroraOutcome: original_inquiry?.aurora_outcome,
			resultMode: original_inquiry?.result_mode,
		};
	}
};

export const getOwnerExperianSuccessValue = (data: { [key: string]: any }) => ({
	success:
		data?.result_mode === UWCheckResultMode.Valid.name
			? data?.result_mode
			: false,
	auroraOutcome: data?.aurora_outcome,
	resultMode: data?.result_mode,
	isFraud: data?.is_fraud,
	bankruptcyStatus: data?.bankruptcy_status,
});

export const getTINCheckSuccessValue = (data: any) => {
	const { original_inquiry, re_run_inquiry } = data || {};
	const isSuccessOriginal = original_inquiry?.is_success;
	const isSuccessRerun = re_run_inquiry?.is_success;
	if (re_run_inquiry) {
		return {
			success: isSuccessRerun,
			auroraOutcome: re_run_inquiry?.aurora_outcome,
			resultMode: re_run_inquiry?.result_mode,
		};
	} else {
		return {
			success: isSuccessOriginal,
			auroraOutcome: original_inquiry?.aurora_outcome,
			resultMode: original_inquiry?.result_mode,
		};
	}
};

export const getMatchSuccessValue = (
	data: matchResponseDataType | undefined,
) => {
	const { original, rerun } = data || {};
	const isSuccessOriginal = original?.success;
	const isSuccessRerun = rerun?.success;
	if (rerun) {
		return {
			success: isSuccessRerun,
			auroraOutcome: rerun?.outcome,
			resultMode: rerun?.result_mode,
			errorMessage: rerun?.error_message,
		};
	} else {
		return {
			success: isSuccessOriginal,
			auroraOutcome: original?.outcome,
			resultMode: original?.result_mode,
			errorMessage: original?.error_message,
		};
	}
};

export const gOfacDataMapAuroraOutcome = ({ data, auroraOutcome }) =>
	data ? { ...data, aurora_outcome: auroraOutcome } : data;

export const matchDataMapper = ({ data, auroraOutcome }) =>
	data
		? {
				...data,
				city: data?.original_inquiry?.address?.city,
				region_code: data?.original_inquiry?.address?.region_code,
				outcome: auroraOutcome,
		  }
		: data;

export const sortPossibleMatches = ({
	sortBy,
	matches,
}: {
	sortBy?: { [key: string]: any }[];
	matches: { [key: string]: any }[];
}) => {
	const sortName: string = sortBy?.[0]?.id;
	const isAscending: boolean = sortBy?.[0]?.desc;

	const sortDate = orderBy(
		matches,
		[sortName === "date" ? "date_to_sort" : sortName], // Fix to sort date by date_to_sort column which contains timestamp
		[isAscending ? "asc" : "desc"],
	);

	return sortDate;
};

export const renderComparisonValue = value => {
	if (!isEmpty(value?.value)) return value.value;
	else if (typeof value === "string") return value;
	else return "";
};

const getAddressRecords = records => {
	if (!Array.isArray(records) || records?.length <= 0) return;

	return records?.map(record => [
		{ label: "Address Line 1", value: record?.address_line1 },
		{ label: "City", value: record?.city },
		{ label: "State", value: record?.region_code },
		{ label: "Zip", value: record?.postal_code },
		{
			label: "Date Reported",
			value: getFormattedDate({
				date: record?.date_reported,
				format: "MM/DD/YYYY",
			}),
		},
		{ label: "Status", value: record?.status },
	]);
};

const getPhoneNumberRecords = records => {
	if (!Array.isArray(records) || records?.length <= 0) return;
	return records?.map(record => [
		{ label: "Phone Number", value: onlyNumberPhone(record?.phone_number) },
		{ label: "Type", value: record?.type },
		{ label: "Classification", value: record?.classification },
	]);
};

export const paginateForTable = (setFetchTableValue, fetchData, pageIndex) => {
	return setFetchTableValue({
		...fetchData,
		page: pageIndex,
		paginationFirstCount:
			fetchData.paginationFirstCount === 0
				? 50
				: fetchData.page < pageIndex
				? fetchData.paginationFirstCount + 50
				: fetchData.paginationFirstCount - 50,
		paginationLastCount:
			fetchData.page < pageIndex
				? fetchData.paginationLastCount + 50
				: fetchData.paginationLastCount - 50,
	});
};
